// ==================== WHITEPAPERS PAGE ====================

import { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import {
  FileText,
  Mail,
  Download,
  ExternalLink,
  CheckCircle2,
  BookOpen,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Clock,
  AlertTriangle,
  X,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { WhitepaperAccessModal } from '../../components/common/WhitepaperAccessModal';
import type { WhitepaperAccessAction } from '../../services';
import { ROUTES, WHITEPAPERS } from '../../constants';
import type { Whitepaper, WhitepaperPreview } from '../../constants';
import { fadeInUp, staggerContainer } from '../../utils/animations';

const AUTO_PLAY_INTERVAL = 4000;
const SWIPE_THRESHOLD = 60;

const slideVariants = {
  enter: (direction: number) => ({ x: direction > 0 ? '100%' : '-100%', opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({ x: direction > 0 ? '-100%' : '100%', opacity: 0 }),
};

const sliderArrowClass =
  'absolute top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm border border-white/15 flex items-center justify-center text-white/80 hover:bg-(--color-signal-orange) hover:border-(--color-signal-orange) hover:text-white transition-all duration-200 cursor-pointer';

const WhitepaperPreviewSlider = ({ title, slides }: { title: string; slides: WhitepaperPreview[] }) => {
  const [[activeIndex, direction], setSlide] = useState<[number, number]>([0, 0]);
  const [isPaused, setIsPaused] = useState(false);
  const reduceMotion = useReducedMotion();

  const paginate = useCallback(
    (dir: number) => setSlide(([index]) => [(index + dir + slides.length) % slides.length, dir]),
    [slides.length]
  );

  const goTo = (index: number) => setSlide(([current]) => [index, index > current ? 1 : -1]);

  // Preload every page so slides don't flash in on first view
  useEffect(() => {
    slides.forEach(({ src }) => {
      const img = new Image();
      img.src = src;
    });
  }, [slides]);

  // Restart the countdown on every slide change so manual navigation isn't cut short
  useEffect(() => {
    if (isPaused || reduceMotion) return;
    const timer = setTimeout(() => paginate(1), AUTO_PLAY_INTERVAL);
    return () => clearTimeout(timer);
  }, [activeIndex, isPaused, reduceMotion, paginate]);

  const slide = slides[activeIndex];

  return (
    <div
      className="max-w-sm mx-auto"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
    >
      <div
        className="relative aspect-640/906 rounded-xl overflow-hidden border border-(--color-border-gray) shadow-2xl bg-(--color-midnight-navy) focus:outline-none focus-visible:ring-2 focus-visible:ring-(--color-signal-orange)"
        role="region"
        aria-roledescription="carousel"
        aria-label={`${title} page previews`}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') paginate(-1);
          if (e.key === 'ArrowRight') paginate(1);
        }}
      >
        <AnimatePresence initial={false} custom={direction}>
          <motion.img
            key={activeIndex}
            src={slide.src}
            alt={`${title}, page ${slide.page}: ${slide.label}`}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.45, ease: 'easeInOut' }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.6}
            onDragEnd={(_, { offset, velocity }) => {
              if (offset.x < -SWIPE_THRESHOLD || velocity.x < -500) paginate(1);
              else if (offset.x > SWIPE_THRESHOLD || velocity.x > 500) paginate(-1);
            }}
            draggable={false}
            className="absolute inset-0 w-full h-full object-cover select-none cursor-grab active:cursor-grabbing"
          />
        </AnimatePresence>

        <button
          type="button"
          onClick={() => paginate(-1)}
          className={`${sliderArrowClass} left-3`}
          aria-label="Previous page"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          type="button"
          onClick={() => paginate(1)}
          className={`${sliderArrowClass} right-3`}
          aria-label="Next page"
        >
          <ChevronRight size={20} />
        </button>

        <span className="absolute top-3 right-3 z-10 px-3 py-1 rounded-full text-xs font-semibold bg-black/60 text-white">
          {activeIndex + 1} / {slides.length}
        </span>
      </div>

      {/* Caption + progress dots */}
      <div className="mt-4 flex items-center justify-between gap-4">
        <p className="min-w-0 text-sm text-(--color-soft-gray) truncate" aria-live={isPaused ? 'polite' : 'off'}>
          <span className="text-(--color-signal-orange) font-semibold">Page {slide.page}</span> · {slide.label}
        </p>
        <div className="flex items-center gap-2 shrink-0">
          {slides.map((preview, i) => (
            <button
              key={preview.page}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`Show page ${preview.page}`}
              aria-current={i === activeIndex}
              className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                i === activeIndex ? 'w-8 bg-(--color-signal-orange)' : 'w-2 bg-white/20 hover:bg-white/40'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

interface WhitepaperCardProps {
  whitepaper: Whitepaper;
  onRequestAccess: (whitepaper: Whitepaper, action: WhitepaperAccessAction) => void;
}

const WhitepaperCard = ({ whitepaper, onRequestAccess }: WhitepaperCardProps) => {
  return (
    <motion.article variants={fadeInUp}>
      <Card variant="highlight" className="overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          {/* Page previews */}
          <div className="lg:col-span-2 min-w-0">
            <WhitepaperPreviewSlider title={whitepaper.title} slides={whitepaper.previewImages} />
          </div>

          {/* Details */}
          <div className="lg:col-span-3">
            <Badge variant="orange" className="mb-4">{whitepaper.category}</Badge>

            <h2 className="text-2xl lg:text-3xl font-heading font-bold text-white mb-3">
              {whitepaper.title}
            </h2>
            <p className="text-lg text-(--color-signal-orange) mb-4">{whitepaper.subtitle}</p>
            <p className="text-(--color-soft-gray) leading-relaxed mb-6">{whitepaper.description}</p>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-(--color-muted-gray) mb-8">
              <span className="inline-flex items-center gap-2">
                <BookOpen size={16} />
                {whitepaper.pages} pages
              </span>
              <span className="inline-flex items-center gap-2">
                <FileText size={16} />
                PDF · {whitepaper.fileSize}
              </span>
            </div>

            {/* Important modifier: the unlayered mobile h3 rule in index.css would otherwise win */}
            <h3 className="text-lg! font-heading font-semibold text-white mb-4">What's inside</h3>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mb-8">
              {whitepaper.sections.map((section) => (
                <li key={section} className="flex items-start gap-2 text-sm text-(--color-soft-gray)">
                  <CheckCircle2 size={16} className="text-(--color-signal-orange) mt-0.5 shrink-0" />
                  {section}
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="primary"
                size="lg"
                leftIcon={<Download size={18} />}
                onClick={() => onRequestAccess(whitepaper, 'download')}
              >
                Download PDF
              </Button>
              <Button
                variant="secondary"
                size="lg"
                leftIcon={<ExternalLink size={18} />}
                onClick={() => onRequestAccess(whitepaper, 'read')}
              >
                Read Online
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </motion.article>
  );
};

// ==================== LINK ERROR NOTICE ====================
// The API redirects here with ?link=<reason> when a whitepaper link can't be opened

type LinkError = 'expired' | 'invalid' | 'unavailable';

const LINK_ERROR_MESSAGES: Record<LinkError, { title: string; message: string; canRetry: boolean }> = {
  expired: {
    title: 'This link has expired',
    message: 'For your security, whitepaper links are valid for 10 minutes. Request a new one — it only takes a moment.',
    canRetry: true,
  },
  invalid: {
    title: "This link isn't valid",
    message: 'The link may be incomplete or out of date. Please request a new one.',
    canRetry: true,
  },
  unavailable: {
    title: 'Whitepaper temporarily unavailable',
    message: "We couldn't load the whitepaper right now. Please try again in a few minutes, or contact us if it keeps happening.",
    canRetry: false,
  },
};

const isLinkError = (value: string | null): value is LinkError =>
  value !== null && Object.hasOwn(LINK_ERROR_MESSAGES, value);

interface LinkErrorNoticeProps {
  error: LinkError;
  onRetry: (action: WhitepaperAccessAction) => void;
  onDismiss: () => void;
}

const LinkErrorNotice = ({ error, onRetry, onDismiss }: LinkErrorNoticeProps) => {
  const { title, message, canRetry } = LINK_ERROR_MESSAGES[error];

  return (
    <motion.div
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      role="alert"
      className="relative flex flex-col sm:flex-row sm:items-center gap-4 p-5 pr-12 rounded-2xl bg-(--color-warning)/10 border border-(--color-warning)/30"
    >
      <div className="w-11 h-11 rounded-xl bg-(--color-warning)/15 flex items-center justify-center shrink-0">
        {error === 'expired' ? (
          <Clock size={22} className="text-(--color-warning)" />
        ) : (
          <AlertTriangle size={22} className="text-(--color-warning)" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-white">{title}</p>
        <p className="text-sm text-(--color-soft-gray) mt-1">{message}</p>
      </div>
      {canRetry ? (
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button variant="primary" size="sm" leftIcon={<Download size={16} />} onClick={() => onRetry('download')}>
            Download PDF
          </Button>
          <Button variant="secondary" size="sm" leftIcon={<ExternalLink size={16} />} onClick={() => onRetry('read')}>
            Read Online
          </Button>
        </div>
      ) : (
        <Link to={ROUTES.CONTACT} className="shrink-0">
          <Button variant="secondary" size="sm" leftIcon={<Mail size={16} />}>
            Contact Us
          </Button>
        </Link>
      )}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="absolute top-3 right-3 p-1.5 rounded-lg text-(--color-muted-gray) hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
      >
        <X size={18} />
      </button>
    </motion.div>
  );
};

const WhitepapersPage = () => {
  const hasWhitepapers = WHITEPAPERS.length > 0;
  const [searchParams, setSearchParams] = useSearchParams();
  const linkError = searchParams.get('link');
  // The whitepaper the failed link was for (falls back to the first one)
  const linkWhitepaper =
    WHITEPAPERS.find((w) => w.id === searchParams.get('whitepaper')) ?? WHITEPAPERS[0];

  // One lead form for the page; it hands the visitor the PDF once submitted.
  // A new key on every open remounts it so the form and result start fresh.
  const [access, setAccess] = useState<{ whitepaper: Whitepaper; action: WhitepaperAccessAction } | null>(null);
  const [isAccessOpen, setIsAccessOpen] = useState(false);
  const [accessFormKey, setAccessFormKey] = useState(0);

  const clearLinkError = () => {
    setSearchParams((params) => {
      params.delete('link');
      params.delete('whitepaper');
      return params;
    }, { replace: true });
  };

  const openAccessForm = (whitepaper: Whitepaper, action: WhitepaperAccessAction) => {
    clearLinkError();
    setAccess({ whitepaper, action });
    setAccessFormKey((key) => key + 1);
    setIsAccessOpen(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className="relative py-16 lg:py-20 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-linear-to-b from-(--color-midnight-navy) via-(--color-dark-navy) to-(--color-midnight-navy)" />
        <motion.div
          className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-(--color-signal-orange)/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />

        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-2xl mx-auto"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-(--color-orange-glow) mb-6"
              variants={fadeInUp}
            >
              <FileText size={32} className="text-(--color-signal-orange)" />
            </motion.div>

            {!hasWhitepapers && (
              <motion.div variants={fadeInUp}>
                <Badge variant="orange" className="mb-4">Coming Soon</Badge>
              </motion.div>
            )}

            <motion.h1
              className="text-4xl lg:text-5xl font-heading font-bold text-white mb-4"
              variants={fadeInUp}
            >
              Whitepapers
            </motion.h1>

            <motion.p
              className="text-lg text-(--color-soft-gray)"
              variants={fadeInUp}
            >
              {hasWhitepapers
                ? 'In-depth guides on WhatsApp Business messaging, AI-powered lead generation and customer engagement. Free to read and download.'
                : "We're preparing in-depth whitepapers on WhatsApp Business messaging, AI-powered lead generation and customer engagement. Check back soon."}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Whitepapers List */}
      {hasWhitepapers && (
        <section className="py-12 lg:py-16">
          <div className="container-custom">
            <AnimatePresence>
              {isLinkError(linkError) && (
                <div className="mb-10">
                  <LinkErrorNotice
                    error={linkError}
                    onRetry={(action) => openAccessForm(linkWhitepaper, action)}
                    onDismiss={clearLinkError}
                  />
                </div>
              )}
            </AnimatePresence>

            <motion.div
              className="space-y-10"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              {WHITEPAPERS.map((whitepaper) => (
                <WhitepaperCard key={whitepaper.id} whitepaper={whitepaper} onRequestAccess={openAccessForm} />
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {access && (
        <WhitepaperAccessModal
          key={accessFormKey}
          isOpen={isAccessOpen}
          onClose={() => setIsAccessOpen(false)}
          whitepaperId={access.whitepaper.id}
          whitepaperTitle={access.whitepaper.title}
          action={access.action}
        />
      )}

      {/* CTA Section */}
      <section className="py-16 lg:py-20">
        <div className="container-custom">
          <motion.div
            className="text-center max-w-2xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2
              className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4"
              variants={fadeInUp}
            >
              Ready to put it into <span className="text-gradient">practice?</span>
            </motion.h2>
            <motion.p className="text-lg text-(--color-soft-gray) mb-8" variants={fadeInUp}>
              Start with one clear objective and see how AmplifyMsg turns conversations into customers.
            </motion.p>
            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to={ROUTES.AUTH.REGISTER}>
                <Button variant="primary" size="lg" rightIcon={<ArrowRight size={18} />}>
                  Get Started
                </Button>
              </Link>
              <Link to={ROUTES.CONTACT}>
                <Button variant="secondary" size="lg" leftIcon={<Mail size={18} />}>
                  Contact Us
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export { WhitepapersPage };
